import { useState, useEffect, useRef } from 'react'
import { CYCLE_LANGS, EN_LANG, L1, L2 } from '../../constants/data'

/**
 * TypingName — flicker-free multi-language hero name animation.
 *
 * Root causes of the previous flickering, and how each is fixed:
 *
 * 1. Per-character setState → React re-render every ~80 ms
 *    Fix: all text content is written directly via ref.current.textContent.
 *    React state is only updated for infrequent structural changes
 *    (badge update on language switch, showFinal toggle, cpos for cursor line).
 *
 * 2. Bricolage Grotesque lacks CJK glyphs → font-swap on every character
 *    Fix: explicit font-family on the cycle span includes Hiragino / Noto CJK
 *    fallbacks so the browser never needs to trigger a font swap.
 *
 * 3. Mount/unmount flicker on cycle → English transition
 *    Fix: a 60 ms wait after setFinal(true/false) gives React time to commit
 *    the new DOM before the animation writes into the fresh refs.
 *
 * 4. GPU paint flicker
 *    Fix: willChange:"contents" + transform:"translateZ(0)" on the h1
 *    promotes it to its own compositor layer.
 */
export function TypingName() {
  /* ── state: only for infrequent structural changes ─────────── */
  const [badge, setBadge] = useState({
    flag:'', label:'', script:'', color:'#00d4ff',
    borderColor:'transparent', show:false,
  })
  const [showFinal, setFinal] = useState(false)
  const [cpos,      setCpos]  = useState('cycle') // 'l1' | 'l2'

  /* ── refs: direct DOM text/colour updates — zero re-renders ── */
  const cycleRef = useRef()   // <span> text for JP / ZH
  const curCRef  = useRef()   // blinking cursor for cycle phase
  const l1Ref    = useRef()   // "Devamkumar"
  const l2Ref    = useRef()   // "Patel"
  const kill     = useRef(false)
  const curTxt   = useRef('')

  const setText  = (ref, txt) => { if (ref.current) ref.current.textContent = txt }

  useEffect(() => {
    kill.current = false
    const w   = ms => new Promise(r => setTimeout(r, ms))
    const jit = (b, v = 14) => Math.max(12, b + Math.random() * v - v / 2)

    const showBadge = (lang, fin) => setBadge({
      flag: lang.flag, label: lang.label,
      script: lang.script ? `· ${lang.script}` : '',
      color: lang.color,
      borderColor: fin ? 'rgba(0,212,255,.38)' : lang.color + '44',
      show: true,
    })

    /* typeIn — writes directly to the cycle span DOM node */
    const typeIn = async (text, spd, col) => {
      curTxt.current = text
      if (cycleRef.current) cycleRef.current.style.color = col
      if (curCRef.current)  curCRef.current.style.background = col
      for (let i = 0; i <= text.length; i++) {
        if (kill.current) return
        setText(cycleRef, text.slice(0, i))
        await w(jit(spd))
      }
    }

    const eraseEl = async spd => {
      const t = curTxt.current
      for (let i = t.length; i >= 0; i--) {
        if (kill.current) return
        setText(cycleRef, t.slice(0, i))
        await w(jit(spd, 8))
      }
    }

    ;(async () => {
      await w(400)
      while (!kill.current) {

        /* ── Japanese & Mandarin ───────────────────────────── */
        for (const lang of CYCLE_LANGS) {
          showBadge(lang, false)
          await typeIn(lang.text, lang.type, lang.color)
          if (kill.current) break
          await w(jit(720, 120))
          await eraseEl(lang.era)
          await w(jit(210, 60))
        }
        if (kill.current) break

        /* ── English two-line ──────────────────────────────── */
        showBadge(EN_LANG, true)
        setFinal(true)
        setCpos('l1')
        await w(60)            // wait for React to mount English spans
        setText(l1Ref, '')
        setText(l2Ref, '')

        for (let i = 0; i <= L1.length; i++) {
          if (kill.current) return
          setText(l1Ref, L1.slice(0, i))
          await w(jit(72, 16))
        }
        await w(260)
        setCpos('l2')

        for (let i = 0; i <= L2.length; i++) {
          if (kill.current) return
          setText(l2Ref, L2.slice(0, i))
          await w(jit(88, 16))
        }
        await w(1900)

        // erase L2
        for (let i = L2.length; i >= 0; i--) {
          if (kill.current) return
          setText(l2Ref, L2.slice(0, i))
          await w(jit(34, 8))
        }
        setCpos('l1')

        // erase L1
        for (let i = L1.length; i >= 0; i--) {
          if (kill.current) return
          setText(l1Ref, L1.slice(0, i))
          await w(jit(34, 8))
        }

        setFinal(false)
        setCpos('cycle')
        await w(60)            // wait for React to remount cycle spans

        /* ── loop ─────────────────────────────────────────── */
      }
    })()

    return () => { kill.current = true }
  }, [])

  return (
    <div>
      {/* language badge */}
      <div
        className={`lang-badge${badge.show ? ' show' : ''}`}
        style={{
          color: badge.color,
          borderColor: badge.borderColor,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,.08),0 0 14px ${badge.color}28`,
        }}
      >
        <span className="lang-flag">{badge.flag}</span>
        <span>{badge.label}</span>
        <span className="lang-script">{badge.script}</span>
      </div>

      {/*
        willChange + translateZ → composite layer prevents paint flicker.
        min-height holds vertical space while both phases transition.
      */}
      <h1
        className="hn"
        style={{ willChange: 'contents', transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
      >
        {/* ── cycle phase ── */}
        {!showFinal && (
          <>
            <span
              ref={cycleRef}
              style={{
                /*
                 * CJK font stack: Hiragino (macOS/iOS), Noto Sans (Android/Linux),
                 * Microsoft YaHei (Windows) — avoids font-swap on every CJK glyph
                 * when Bricolage Grotesque is the headline font.
                 */
                fontFamily:
                  "var(--disp),'Hiragino Sans','Noto Sans CJK SC'," +
                  "'Microsoft YaHei',system-ui,-apple-system,sans-serif",
              }}
            />
            <span
              ref={curCRef}
              className="tcur"
              style={{ height: '.78em', display: 'inline-block', background: '#00d4ff' }}
            />
          </>
        )}

        {/* ── English phase ── */}
        {showFinal && (
          <>
            <span className="n1" ref={l1Ref}/>
            {cpos === 'l1' && (
              <span className="tcur tcur1" style={{ display: 'inline-block' }}/>
            )}
            <br/>
            <span className="n2" ref={l2Ref}/>
            {cpos === 'l2' && (
              <span className="tcur tcur2" style={{ display: 'inline-block' }}/>
            )}
          </>
        )}
      </h1>
    </div>
  )
}
